import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  estimateReadTimeMinutes,
  fetchBlogPostBySlug,
  fetchBlogPosts,
  formatBlogDate,
  type BlogPost,
} from '../lib/blogFirestore';

function encode(value: string) {
  return encodeURIComponent(value);
}

function buildShareUrls({ url, title }: { url: string; title: string }) {
  const encodedUrl = encode(url);
  const encodedTitle = encode(title);
  return {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    x: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
  };
}

function ShareButtons({ title }: { title: string }) {
  const [copied, setCopied] = useState(false);
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const urls = buildShareUrls({ url: pageUrl, title });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div className="share">
      <a className="share-btn" href={urls.linkedin} target="_blank" rel="noreferrer">
        LinkedIn
      </a>
      <a className="share-btn" href={urls.x} target="_blank" rel="noreferrer">
        X
      </a>
      <a className="share-btn" href={urls.email}>
        Email
      </a>
      <button type="button" className="share-btn" onClick={copyLink}>
        {copied ? 'Copied' : 'Copy link'}
      </button>
    </div>
  );
}

function RelatedPosts({ currentPost, allPosts }: { currentPost: BlogPost; allPosts: BlogPost[] }) {
  const related = allPosts
    .filter((p) => p.slug !== currentPost.slug)
    .filter((p) => {
      const currentTags = currentPost.tags || [];
      const postTags = p.tags || [];
      return currentTags.some((tag) => postTags.includes(tag));
    })
    .slice(0, 3);

  const posts = related.length ? related : allPosts.filter((p) => p.slug !== currentPost.slug).slice(0, 3);

  if (!posts.length) return null;

  return (
    <section className="section blog-related">
      <div className="container">
        <h2 className="blog-related-title">Related Articles</h2>
        <div className="blog-related-grid">
          {posts.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="blog-related-card">
              {p.featured_image && (
                <div className="blog-related-media">
                  <img src={p.featured_image} alt={p.imageAlt || p.title} loading="lazy" />
                </div>
              )}
              <div className="blog-related-body">
                <time className="muted">{formatBlogDate(p.date)}</time>
                <div className="blog-related-titleline">{p.title}</div>
                {p.excerpt && <div className="blog-related-excerpt">{p.excerpt}</div>}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug) return;
      try {
        const [p, all] = await Promise.all([
          fetchBlogPostBySlug(slug, { siteKey: 'tagIt' }),
          fetchBlogPosts({ siteKey: 'tagIt' }),
        ]);
        if (cancelled) return;
        setPost(p);
        setPosts(all);
      } catch (err: any) {
        if (cancelled) return;
        setError((err?.message || 'Failed to load post.').toString());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    if (posts.length && !post) navigate('/blog', { replace: true });
  }, [posts, post, navigate, slug]);

  useEffect(() => {
    document.title = post?.title ? `${post.title} — tagIt Blog` : 'tagIt Blog';
  }, [post?.title]);

  const idx = useMemo(() => (post ? posts.findIndex((p) => p.slug === post.slug) : -1), [posts, post]);
  const prev = idx > 0 ? posts[idx - 1] : undefined;
  const next = idx >= 0 && idx < posts.length - 1 ? posts[idx + 1] : undefined;

  if (error) {
    return (
      <div className="app">
        <Header />
        <main className="section blog">
          <div className="container">
            <div className="card blog-empty">Error: {error}</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="app">
        <Header />
        <main className="section blog">
          <div className="container">
            <div className="card blog-empty">Loading…</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const minutes = estimateReadTimeMinutes(post.content);
  const category = Array.isArray(post.tags) && post.tags.length ? post.tags[0] : null;

  return (
    <div className="app">
      <Header />
      <main className="blog-post">
        <nav className="container blog-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" className="muted">
            Home
          </Link>
          <span className="muted">/</span>
          <Link to="/blog" className="muted">
            Blog
          </Link>
          {category && (
            <>
              <span className="muted">/</span>
              <span>{category}</span>
            </>
          )}
        </nav>

        <header className="blog-hero">
          {post.featured_image ? (
            <img className="blog-hero-image" src={post.featured_image} alt={post.imageAlt || post.title} />
          ) : (
            <div className="blog-hero-fallback" />
          )}
          <div className="blog-hero-overlay">
            <div className="container blog-hero-inner">
              {category && <span className="badge badge-solid">{category}</span>}
              <h1 className="blog-hero-title">{post.title}</h1>
            </div>
          </div>
        </header>

        <div className="blog-meta">
          <div className="container blog-meta-inner">
            <span className="blog-meta-item">
              <span className="blog-meta-strong">By {post.author}</span>
            </span>
            <span className="muted">•</span>
            <time className="muted" dateTime={post.date}>
              {formatBlogDate(post.date)}
            </time>
            <span className="muted">•</span>
            <span className="muted">{minutes} min read</span>
          </div>
        </div>

        <section className="section blog-post-body">
          <div className="container blog-post-layout">
            <aside className="blog-post-aside">
              <ShareButtons title={post.title} />
            </aside>
            <div className="blog-prose" dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>
        </section>

        <section className="section blog-author">
          <div className="container">
            <div className="card blog-author-card">
              <div className="blog-author-name">{post.author}</div>
              {post.authorBio && <div className="blog-author-bio">{post.authorBio}</div>}
              {!post.authorBio && (
                <div className="blog-author-bio">
                  AdTech Product Architect focused on Relevance and Impact. Expertise in bridging product strategy with
                  technical execution.
                </div>
              )}
              <div className="blog-author-links">
                {post.authorLinks?.linkedin && (
                  <a className="btn btn-ghost btn-sm" href={post.authorLinks.linkedin} target="_blank" rel="noreferrer">
                    LinkedIn
                  </a>
                )}
                {post.authorLinks?.twitter && (
                  <a className="btn btn-ghost btn-sm" href={post.authorLinks.twitter} target="_blank" rel="noreferrer">
                    X
                  </a>
                )}
                {post.authorLinks?.email && (
                  <a className="btn btn-ghost btn-sm" href={`mailto:${post.authorLinks.email}`}>
                    Email
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>

        <RelatedPosts currentPost={post} allPosts={posts} />

        <section className="section blog-nav">
          <div className="container blog-nav-inner">
            <Link to="/blog" className="btn btn-ghost btn-sm">
              ← Back to Blog
            </Link>
            <div className="blog-nav-links">
              {prev && (
                <Link to={`/blog/${prev.slug}`} className="btn btn-ghost btn-sm blog-nav-link" title={prev.title}>
                  ← {prev.title}
                </Link>
              )}
              {next && (
                <Link to={`/blog/${next.slug}`} className="btn btn-ghost btn-sm blog-nav-link" title={next.title}>
                  {next.title} →
                </Link>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

